import { useEffect, useState, useRef } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader, KpiCard } from "@/components/Common";
import { ComplianceBadge } from "@/components/ComplianceBadge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldAlert, AlertTriangle, FileWarning, Download, Upload, Loader2, GraduationCap, FileBadge } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Compliance() {
  const [emps, setEmps] = useState([]);
  const [who, setWho] = useState("employee");
  const [allEmps, setAllEmps] = useState([]);
  useEffect(() => { setEmps(allEmps.filter((e) => (e.worker_type === "rct") === (who === "rct"))); }, [allEmps, who]);
  const [expiring, setExpiring] = useState([]);
  const [upBusy, setUpBusy] = useState(false);
  const [upResult, setUpResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pickedFile, setPickedFile] = useState(null);
  const [overwrite, setOverwrite] = useState(true);
  const fileRef = useRef(null);
  const load = () => {
    api.get("/employees").then((r) => setAllEmps(r.data)).catch(() => {});
    api.get("/documents/expiring", { params: { days: 60 } }).then((r) => setExpiring(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/training/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "training_matrix_template.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };
  const pickMatrix = async (f) => {
    if (!f) return;
    setUpBusy(true); setUpResult(null); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/training/upload", fd, { params: { commit: false }, headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data); setPickedFile(f); setOverwrite(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUpBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const confirmMatrix = async () => {
    if (!pickedFile) return;
    setUpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", pickedFile);
      const res = await api.post("/training/upload", fd, { params: { commit: true, overwrite }, headers: { "Content-Type": "multipart/form-data" } });
      setUpResult(res.data); setPreview(null); setPickedFile(null);
      toast.success(`${res.data.certs_added + res.data.certs_updated} certificates saved`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUpBusy(false); }
  };

  const compliant = emps.filter((e) => e.compliance?.compliant).length;
  const spFail = who === "rct" ? emps.filter((e) => e.compliance && e.compliance.insurance_status !== "valid").length : emps.filter((e) => e.compliance && !e.compliance.safe_pass_ok).length;
  const atRisk = emps.filter((e) => e.compliance && !e.compliance.compliant).length;
  const sorted = [...emps].sort((a, b) => (a.compliance?.pct ?? 0) - (b.compliance?.pct ?? 0));

  const exportCsv = () => {
    const headers = ["Employee", "Employee No", "Compliance %", "Safe Pass", "Outstanding items"];
    const lines = [headers.join(",")];
    sorted.forEach((e) => {
      const c = e.compliance || {};
      const sp = c.safe_pass_ok ? "Valid" : (c.safe_pass_status === "expired" ? "Expired" : "Missing");
      const row = [`${e.first_name} ${e.surname}`, e.employee_number || "", c.pct ?? 0, sp, (c.missing || []).join("; ")]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`);
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `compliance-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout nav={adminNav} title="Compliance">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <PageHeader title="Compliance & Certifications" subtitle="Profile completeness and cert status across the workforce" />
        <div className="flex flex-wrap gap-2 mt-1">
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-0.5" data-testid="compliance-who">
            {[["employee", "Employees"], ["rct", "Subcontractors"]].map(([v, l]) => <button key={v} type="button" data-testid={`compliance-who-${v}`} onClick={() => setWho(v)} className={`px-3 py-1.5 text-sm rounded ${who === v ? "bg-slate-900 text-white" : "text-zinc-600"}`}>{l}</button>)}
          </div>
          <Link to="/admin/compliance/certificates" data-testid="compliance-staff-certs"><Button className="bg-slate-900 text-white"><FileBadge className="h-4 w-4 mr-1" />Staff certificates</Button></Link>
          <Button data-testid="compliance-export" variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1" />Export CSV</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard testId="comp-total" label={who === "rct" ? "Subcontractors" : "Total Staff"} value={emps.length} icon={ShieldCheck} />
        <KpiCard testId="comp-compliant" label="Fully Compliant" value={compliant} icon={ShieldCheck} />
        <KpiCard testId="comp-atrisk" label="Action Needed" value={atRisk} icon={AlertTriangle} accent />
        <KpiCard testId="comp-safepass" label={who === "rct" ? "Insurance Issues" : "Safe Pass Issues"} value={spFail} icon={ShieldAlert} />
      </div>

      {/* Training matrix upload */}
      <div className="bg-white border border-zinc-200 rounded-md mb-6 p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-md bg-slate-900 text-white flex items-center justify-center shrink-0"><GraduationCap className="h-5 w-5" /></div>
            <div>
              <div className="font-heading font-bold text-slate-900">Upload training matrix</div>
              <p className="text-sm text-zinc-500 mt-0.5 max-w-2xl">Upload your whole training spreadsheet (CSV or Excel). Each "Yes" cell saves that certificate + expiry onto the matching employee — compliance % and expiry alerts update automatically. Certificates are only added or updated, never removed. Unmatched names are skipped and listed back.</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button data-testid="training-template-btn" variant="outline" onClick={downloadTemplate}><Download className="h-4 w-4 mr-1" />Template</Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="training-upload-input" onChange={(e) => pickMatrix(e.target.files?.[0])} />
            <Button data-testid="training-upload-btn" className="bg-slate-900 text-white" disabled={upBusy} onClick={() => fileRef.current?.click()}>{upBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Upload sheet</>}</Button>
          </div>
        </div>
        {preview && (
          <div data-testid="training-preview" className="mt-4 border-t border-zinc-100 pt-3">
            <div className="flex flex-wrap gap-2 text-xs mb-3">
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700 font-semibold">{preview.counts.matched} employees matched</span>
              <span className="px-2 py-1 rounded bg-sky-100 text-sky-700 font-semibold">{preview.counts.new_certs} new certificates</span>
              <span className="px-2 py-1 rounded bg-amber-100 text-amber-800 font-semibold">{preview.counts.existing_certs} already on file</span>
              {preview.counts.unmatched > 0 && <span className="px-2 py-1 rounded bg-rose-100 text-rose-700 font-semibold">{preview.counts.unmatched} unmatched</span>}
            </div>
            {preview.counts.existing_certs > 0 && (
              <label className="flex items-start gap-2 text-sm rounded-md border border-amber-200 bg-amber-50 p-3 cursor-pointer mb-3">
                <input type="checkbox" data-testid="training-overwrite" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="mt-0.5" />
                <span className="text-amber-800">Overwrite the expiry dates on the {preview.counts.existing_certs} certificate(s) already on file. <span className="text-amber-600">Untick to keep existing dates and only add the new ones.</span></span>
              </label>
            )}
            {preview.unmatched?.length > 0 && <div className="text-xs text-amber-700 mb-2">Unmatched (will be skipped): {preview.unmatched.join(", ")}</div>}
            {preview.errors?.length > 0 && <ul className="text-xs text-rose-600 list-disc pl-5 mb-2">{preview.errors.map((x, i) => <li key={i}>{x}</li>)}</ul>}
            <div className="flex gap-2">
              <Button variant="outline" data-testid="training-cancel" onClick={() => { setPreview(null); setPickedFile(null); }}>Cancel</Button>
              <Button data-testid="training-confirm" className="bg-slate-900 text-white" disabled={upBusy} onClick={confirmMatrix}>{upBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & save"}</Button>
            </div>
          </div>
        )}
        {upResult && (
          <div data-testid="training-upload-result" className="mt-4 text-sm border-t border-zinc-100 pt-3">
            <div className="text-emerald-700 font-medium">{upResult.certs_added} added · {upResult.certs_updated} updated{upResult.certs_skipped ? ` · ${upResult.certs_skipped} kept` : ""} · {upResult.employees_matched} employees matched (of {upResult.total_rows} rows)</div>
            {upResult.unmatched?.length > 0 && <div className="mt-1 text-amber-700">Unmatched (skipped): {upResult.unmatched.join(", ")}</div>}
            {upResult.errors?.length > 0 && <ul className="mt-1 text-rose-600 list-disc pl-5">{upResult.errors.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          </div>
        )}
      </div>

      {/* Expiring / expired certs */}
      <div className="bg-white border border-zinc-200 rounded-md mb-6">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
          <FileWarning className="h-4 w-4 text-amber-500" /> Certs expiring or expired (next 60 days)
        </div>
        <div className="divide-y divide-zinc-50">
          {expiring.length === 0 && <div className="px-4 py-3 text-sm text-zinc-400" data-testid="expiring-empty">No certs expiring in the next 60 days.</div>}
          {expiring.map((d) => (
            <div key={d.id} data-testid={`expiring-${d.id}`} className="px-4 py-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{d.doc_type} — {d.employee_name}</div>
                <div className="text-xs text-zinc-500">Expiry {fmtDate(d.expiry)}</div>
              </div>
              <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${d.expired ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"}`}>
                {d.expired ? "Expired" : "Expiring soon"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-employee compliance */}
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Employee</th><th className="p-3">Compliance</th><th className="p-3">{who === "rct" ? "Insurance" : "Safe Pass"}</th><th className="p-3">Outstanding items</th>
          </tr></thead>
          <tbody>
            {sorted.map((e) => (
              <tr key={e.id} data-testid={`comp-row-${e.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 font-medium whitespace-nowrap">{e.first_name} {e.surname}</td>
                <td className="p-3"><ComplianceBadge c={e.compliance} testId={`comp-badge-${e.id}`} /></td>
                <td className="p-3">
                  {e.worker_type === "rct" ? (
                    <span data-testid={`comp-ins-${e.id}`} className={`text-xs font-semibold ${e.compliance?.insurance_status === "valid" ? "text-emerald-600" : "text-rose-600"}`}>
                      {e.compliance?.insurance_status === "valid" ? "Valid" : e.compliance?.insurance_status === "expired" ? "Expired" : "Missing"}{e.compliance?.insurance_expiry ? ` · ${e.compliance.insurance_expiry}` : ""}
                    </span>
                  ) : (
                  <span className={`text-xs font-semibold ${e.compliance?.safe_pass_ok ? "text-emerald-600" : "text-rose-600"}`}>
                    {e.compliance?.safe_pass_ok ? "Valid" : (e.compliance?.safe_pass_status === "expired" ? "Expired" : "Missing")}
                  </span>)}
                </td>
                <td className="p-3 text-xs text-zinc-500">{e.compliance?.missing?.length ? e.compliance.missing.join(", ") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
